import React from 'react'
import { observable, action, makeObservable } from 'mobx'
import i18n from 'i18next'
import { v4 as uuidv4 } from 'uuid'
import * as R from 'ramda'
import queryString from 'query-string'
import * as Widgets from './SalesIntelligenceWidgets'
import { getProductMap } from '../../utils/helpers'
import { MIXPANEL_EVENTS } from '../../utils/MixpanelManager'

class SalesIntelligenceStore {
    @observable product

    @observable surveyId

    @observable products

    @observable companyPageData

    @observable page = 'companyView' // exhibitorView, eventPicker

    @observable company

    @observable loading = false

    @observable error = ''

    @observable shared = false

    productMap = {}

    constructor(store, shared) {
        makeObservable(this)
        this.appStore = store
        this.shared = shared
    }

    init = async () => {
        const { location } = window
        const params = queryString.parse(location.search)
        const { shared } = this
        if (!shared) {
            await this.getProducts()
            this.product = this.products.find((p) => p.productId === Number(params.productId))
            const productMap = await getProductMap(this.appStore.http, this.appStore.userData.clientId)
            this.productMap = productMap
        } else {
            await this.getSharedProducts()
            this.product = this.products.find((p) => p.productId === Number(params.productId))
        }
        this.getCompany()
        if (this.company) {
            this.page = 'exhibitorView'
            this.changeExhibitor({ value: this.company })
        }
    }

    initCompanyPage = async () => {
        const { shared } = this
        try {
            this.error = ''
            const { appStore } = this

            const companyPageData = await appStore.http.post(
                `/salesIntelligence/company${shared ? '/shared' : ''}`,
                JSON.stringify({ productId: this.product.productId }),
            )

            this.surveyId = companyPageData.surveyId

            this.companyPageData = {
                salesIntelligenceMatrix: companyPageData.salesIntelligenceMatrix,
                exhibitorList: companyPageData.exhibitorList,
            }
        } catch (e) {
            console.log('error: ', e)
            this.error = 'noData'
            this.loading = false
        }
    }

    initExhibitorPage = async () => {
        try {
            this.error = ''
            this.loading = true
            const { shared } = this
            const { appStore, company } = this
            const exhibitorPageData = await appStore.http.post(
                `/salesIntelligence/exhibitor${shared ? '/shared' : ''}`,
                JSON.stringify({ productId: this.product.productId, companyName: company }),
            )

            this.surveyId = exhibitorPageData.surveyId

            this.exhibitorPageData = {
                EEI: exhibitorPageData.EEI,
                ECI: exhibitorPageData.ECI,
                NPS: exhibitorPageData.NPS,
                LoR: exhibitorPageData.LoR,
                objectives: exhibitorPageData.objectives,
                salesIntelligenceMatrix: exhibitorPageData.salesIntelligenceMatrix,
                allTextQuestions: exhibitorPageData.allTextQuestions,
            }
            this.loading = false
        } catch (e) {
            console.log('error: ', e)
            if (e?.message === 'Objectives Question not found') {
                this.error = 'noObjectivesQuestion'
            } else {
                this.error = 'noData'
            }
            this.loading = false
        }
    }

    changePage = (page) => { this.page = page }

    getCompany = () => {
        const url = new URL(window.location.href)

        const company = url.searchParams.get('companyName')

        if (company === null) {
            return false
        }

        this.company = company
        return true
    }

    getProducts = async () => {
        const { appStore } = this
        try {
            const products = await appStore.http.get('/product?salesIntelligence=true')
            this.products = products
        } catch (e) {
            return {}
        }
    }

    getSharedProducts = async () => {
        const { appStore } = this
        try {
            const products = await appStore.http.get('/salesIntelligence/products/shared')
            this.products = products
        } catch (e) {
            return {}
        }
    }

    changeExhibitor = ({ value }) => {
        this.company = value
        const url = new URL(window.location.href)

        url.searchParams.set('companyName', value)

        window.history.replaceState({}, '', url)
        this.initExhibitorPage()
    }

    /** Mixpanel */
    trackSI = (eventName, properties = {}) => {
        const { appStore } = this
        appStore.mixpanel?.track(eventName, {
            product_id: R.pathOr(undefined, ['product', 'productId'], this),
            ...properties,
        })
    }

    trackSIEventLevelOpened = () => this.trackSI(MIXPANEL_EVENTS.SI_EVENT_LEVEL_OPENED)

    trackSIExhibitorLevelOpened = () => this.trackSI(MIXPANEL_EVENTS.SI_EXHIBITOR_LEVEL_OPENED)

    trackSIExhibitorFilterChange = () => this.trackSI(MIXPANEL_EVENTS.SI_EXHIBITOR_FILTER_CHANGE)
    /** End MixPanel */
}

export default SalesIntelligenceStore
